import React, { useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../Config";

import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

const Domicilios = () => {
  const { baseUrl } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);

  const [domicilios, setDomicilios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [medidores, setMedidores] = useState([]);
  const [remarcadores, setRemarcadores] = useState([]);

  const [filtro, setFiltro] = useState("");

  const [dialogVisible, setDialogVisible] = useState(false);
  const [modoDialog, setModoDialog] = useState("crear");

  const [form, setForm] = useState({
    id: null,
    codigo: "",
    descripcion: "",
    direccion: "",
    idCliente: null,
    idsMedidores: [],
  });

  const [errores, setErrores] = useState({});
  const [mensaje, setMensaje] = useState(null);

  const joinUrl = (base, path) =>
    `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(
      /^\/+/,
      "",
    )}`;

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const normalizar = (valor) =>
    String(valor || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .trim();

  const endpointDisponiblesParaDomicilio = (categoria, idDomicilio = null) => {
    const params = new URLSearchParams({
      categoria,
    });

    if (idDomicilio !== null && idDomicilio !== undefined) {
      params.append("idDomicilio", idDomicilio);
    }

    return joinUrl(
      baseUrl,
      `medidor/getDisponiblesParaDomicilio?${params.toString()}`,
    );
  };

  const limpiarFormulario = () => {
    setForm({
      id: null,
      codigo: "",
      descripcion: "",
      direccion: "",
      idCliente: null,
      idsMedidores: [],
    });
    setErrores({});
    setMensaje(null);
  };

  const cargarClientes = async () => {
    const data = await fetchJson(joinUrl(baseUrl, "cliente/getClientes"), {
      method: "GET",
    });
    setClientes(Array.isArray(data?.data) ? data.data : []);
  };

  const cargarMedidores = async (idDomicilio = null) => {
    const [medData, remData] = await Promise.all([
      fetchJson(endpointDisponiblesParaDomicilio("MEDIDOR", idDomicilio), {
        method: "GET",
      }),
      fetchJson(endpointDisponiblesParaDomicilio("REMARCADOR", idDomicilio), {
        method: "GET",
      }),
    ]);

    setMedidores(Array.isArray(medData?.medidores) ? medData.medidores : []);
    setRemarcadores(Array.isArray(remData?.medidores) ? remData.medidores : []);
  };

  const cargarDomicilios = async () => {
    const data = await fetchJson(joinUrl(baseUrl, "domicilio/getDomicilios"), {
      method: "GET",
    });
    setDomicilios(Array.isArray(data?.data) ? data.data : []);
  };

  const cargarTodo = async () => {
    setIsLoading(true);
    try {
      await Promise.all([cargarClientes(), cargarDomicilios()]);
    } catch (e) {
      console.error(e);
      alert("Error cargando datos de domicilios: " + (e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    cargarTodo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clienteOptions = useMemo(() => {
    return (clientes || []).map((c) => ({
      label: `${c?.razonSocial || "Cliente"}${c?.rut ? ` (${c.rut})` : ""}`,
      value: c?.id,
    }));
  }, [clientes]);

  const medidoresOptions = useMemo(() => {
    const opcionesMedidores = (medidores || [])
      .filter((m) => m?.id != null)
      .map((m) => ({
        label: `Medidor Código: ${m?.codigo ?? "-"}`,
        value: m.id,
      }));

    const opcionesRemarcadores = (remarcadores || [])
      .filter((r) => r?.id != null)
      .map((r) => ({
        label: `Remarcador QR: ${r?.qrCode ?? "-"}`,
        value: r.id,
      }));

    return [...opcionesMedidores, ...opcionesRemarcadores];
  }, [medidores, remarcadores]);

  const domiciliosFiltrados = useMemo(() => {
    const q = normalizar(filtro);
    if (!q) return domicilios;

    return (domicilios || []).filter((d) => {
      const qrTexto = Array.isArray(d?.medidoresQr)
        ? d.medidoresQr.join(" ")
        : Array.isArray(d?.qrsMedidores)
        ? d.qrsMedidores.join(" ")
        : Array.isArray(d?.medidores)
        ? d.medidores
            .map((m) => m?.qrCode)
            .filter(Boolean)
            .join(" ")
        : "";

      const texto = normalizar(
        [
          d?.id,
          d?.codigo,
          d?.descripcion,
          d?.direccion,
          d?.clienteNombre,
          d?.clienteRut,
          qrTexto,
        ]
          .filter(Boolean)
          .join(" "),
      );

      return texto.includes(q);
    });
  }, [domicilios, filtro]);

  const setCampo = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
    setErrores((prev) => ({ ...prev, [campo]: null }));
    setMensaje(null);
  };

  const validarFormulario = () => {
    const next = {};

    if (!String(form.descripcion || "").trim()) {
      next.descripcion = "La descripción es obligatoria.";
    }

    if (!String(form.direccion || "").trim()) {
      next.direccion = "La dirección es obligatoria.";
    }

    if (!form.idCliente) {
      next.idCliente = "Debe seleccionar un cliente.";
    }

    setErrores(next);
    return Object.keys(next).length === 0;
  };

  const abrirCrear = async () => {
    setIsLoading(true);
    try {
      limpiarFormulario();
      setModoDialog("crear");
      await cargarMedidores(null);
      setDialogVisible(true);
    } catch (e) {
      console.error(e);
      alert(
        "No se pudieron cargar los medidores disponibles: " + (e?.message || e),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const abrirEditar = async (row) => {
    setIsLoading(true);
    try {
      limpiarFormulario();
      setModoDialog("editar");

      await cargarMedidores(row.id);

      const data = await fetchJson(
        joinUrl(
          baseUrl,
          `domicilio/obtenerDomicilioDetalle?id=${encodeURIComponent(row.id)}`,
        ),
        { method: "GET" },
      );

      const detalle = data?.data || {};
      const domicilio = detalle?.domicilio || {};
      const cliente = detalle?.cliente || null;
      const meds = Array.isArray(detalle?.medidores) ? detalle.medidores : [];

      setForm({
        id: domicilio?.id ?? row?.id ?? null,
        codigo: domicilio?.codigo ?? row?.codigo ?? "",
        descripcion: domicilio?.descripcion ?? row?.descripcion ?? "",
        direccion: domicilio?.direccion ?? row?.direccion ?? "",
        idCliente: cliente?.id ?? row?.idCliente ?? null,
        idsMedidores: meds.map((m) => m.id).filter(Boolean),
      });

      setDialogVisible(true);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar el detalle del domicilio: " + (e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  const cerrarDialog = () => {
    setDialogVisible(false);
    limpiarFormulario();
  };

  const guardarDomicilio = async () => {
    if (!validarFormulario()) return;

    setIsLoading(true);
    try {
      const payload = {
        domicilio: {
          id: form.id,
          codigo: String(form.codigo || "").trim(),
          descripcion: String(form.descripcion || "").trim(),
          direccion: String(form.direccion || "").trim(),
        },
        idCliente: form.idCliente,
        idsMedidores: form.idsMedidores || [],
      };

      if (modoDialog === "crear") {
        await fetchJson(
          joinUrl(baseUrl, "domicilio/crearDomicilioConRelaciones"),
          {
            method: "POST",
            body: JSON.stringify(payload),
          },
        );
      } else {
        await fetchJson(
          joinUrl(
            baseUrl,
            `domicilio/actualizarDomicilioConRelaciones?id=${encodeURIComponent(
              form.id,
            )}`,
          ),
          {
            method: "PUT",
            body: JSON.stringify(payload),
          },
        );
      }

      await cargarDomicilios();
      cerrarDialog();
    } catch (e) {
      console.error(e);
      setMensaje({
        severity: "error",
        text: e?.message || "No se pudo guardar el domicilio.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clienteBody = (row) => {
    const nombre = row?.clienteNombre || "-";
    const rut = row?.clienteRut || "";

    if (!nombre || nombre === "-") return "-";
    return rut ? `${nombre} (${rut})` : nombre;
  };

  const medidoresBody = (row) => {
    const qrs = Array.isArray(row?.medidoresQr)
      ? row.medidoresQr
      : Array.isArray(row?.qrsMedidores)
      ? row.qrsMedidores
      : Array.isArray(row?.medidores)
      ? row.medidores.map((m) => m?.qrCode).filter(Boolean)
      : [];

    if (!qrs.length) {
      return <Tag value="Sin QR" severity="warning" />;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {qrs.map((qr, index) => (
          <Tag key={`${qr}-${index}`} value={String(qr)} />
        ))}
      </div>
    );
  };

  const accionesBody = (row) => (
    <div className="flex gap-2">
      <Button
        label="Editar"
        icon="pi pi-pencil"
        className="p-button-sm"
        onClick={() => abrirEditar(row)}
      />
    </div>
  );

  const header = (
    <div className="flex flex-column gap-3 md:flex-row md:justify-content-between md:align-items-center">
      <div>
        <h2 className="m-0">Domicilios</h2>
        <small>Crear, editar y relacionar clientes y medidores.</small>
      </div>

      <div className="flex flex-column md:flex-row gap-2">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar por código, descripción, dirección, cliente..."
          />
        </span>

        <Button
          label="Recargar"
          icon="pi pi-refresh"
          className="p-button-secondary"
          onClick={cargarTodo}
        />

        <Button
          label="Nuevo domicilio"
          icon="pi pi-plus"
          onClick={abrirCrear}
        />
      </div>
    </div>
  );

  return (
    <div className="grid">
      <div className="col-12">
        <div className="card">
          {isLoading && (
            <div className="flex justify-content-center my-4">
              <ProgressSpinner />
            </div>
          )}

          {!isLoading && (
            <DataTable
              value={domiciliosFiltrados}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 50]}
              responsiveLayout="scroll"
              dataKey="id"
              header={header}
              emptyMessage="No se encontraron domicilios."
            >
              <Column field="id" header="ID" style={{ width: "6rem" }} />
              {/* <Column field="codigo" header="Código" /> */}
              <Column field="descripcion" header="Descripción" />
              <Column field="direccion" header="Dirección" />
              <Column body={clienteBody} header="Cliente" />
              <Column body={medidoresBody} header="QR Medidores" />
              <Column
                body={accionesBody}
                header="Acciones"
                style={{ width: "10rem" }}
              />
            </DataTable>
          )}
        </div>
      </div>

      <Dialog
        header={modoDialog === "crear" ? "Nuevo Domicilio" : "Editar Domicilio"}
        visible={dialogVisible}
        style={{ width: "60rem", maxWidth: "95vw" }}
        modal
        onHide={cerrarDialog}
      >
        <div className="grid">
          <div className="col-12">
            {mensaje && (
              <Message severity={mensaje.severity} text={mensaje.text} />
            )}
          </div>

          {/*} <div className="col-12 md:col-4">
            <label className="block mb-2">Código</label>
            <InputText
              className="w-full"
              value={form.codigo}
              onChange={(e) => setCampo("codigo", e.target.value)}
            />
            {errores.codigo && (
              <small className="p-error block mt-1">{errores.codigo}</small>
            )}
          </div> */}

          <div className="col-12 md:col-8">
            <label className="block mb-2">Descripción</label>
            <InputText
              className="w-full"
              value={form.descripcion}
              onChange={(e) => setCampo("descripcion", e.target.value)}
            />
            {errores.descripcion && (
              <small className="p-error block mt-1">
                {errores.descripcion}
              </small>
            )}
          </div>

          <div className="col-12">
            <label className="block mb-2">Dirección</label>
            <InputText
              className="w-full"
              value={form.direccion}
              onChange={(e) => setCampo("direccion", e.target.value)}
            />
            {errores.direccion && (
              <small className="p-error block mt-1">{errores.direccion}</small>
            )}
          </div>

          <div className="col-12 md:col-6">
            <label className="block mb-2">Cliente</label>
            <Dropdown
              className="w-full"
              value={form.idCliente}
              options={clienteOptions}
              onChange={(e) => setCampo("idCliente", e.value)}
              placeholder="Seleccione cliente"
              filter
              showClear
            />
            {errores.idCliente && (
              <small className="p-error block mt-1">{errores.idCliente}</small>
            )}
          </div>

          <div className="col-12 md:col-6">
            <label className="block mb-2">Medidores / Remarcadores</label>
            <MultiSelect
              className="w-full"
              value={form.idsMedidores}
              options={medidoresOptions}
              onChange={(e) => setCampo("idsMedidores", e.value)}
              placeholder="Seleccione medidores"
              filter
              display="chip"
            />
          </div>

          <div className="col-12 flex justify-content-end gap-2 mt-3">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={cerrarDialog}
            />
            <Button
              label={modoDialog === "crear" ? "Crear" : "Guardar"}
              icon="pi pi-check"
              onClick={guardarDomicilio}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Domicilios;
